import * as React from 'react';

/**
 * Vector token marks used in the hero BalanceCard. Each is a self-contained
 * SVG (a colored coin disc + the token's white glyph), scaled by `className`.
 */
type TokenIconProps = React.SVGProps<SVGSVGElement>;

/**
 * USD Coin token mark from Circle's official brand kit.
 * https://www.circle.com/pressroom
 */
export function UsdcIcon(props: TokenIconProps) {
  return (
    <svg
      viewBox="0 0 96 96"
      fill="none"
      role="img"
      aria-label="USDC"
      {...props}
    >
      <path
        d="M48 95C73.9574 95 95 73.9574 95 48C95 22.0426 73.9574 1 48 1C22.0426 1 1 22.0426 1 48C1 73.9574 22.0426 95 48 95Z"
        fill="#0B53BF"
      />
      <path
        d="M56.4609 13.7778V19.8291C68.5341 23.4716 77.3759 34.6928 77.3759 47.9997C77.3759 61.3066 68.5341 72.5278 56.4609 76.1703V82.2216C71.8534 78.4616 83.2509 64.5672 83.2509 47.9997C83.2509 31.4322 71.8534 17.5378 56.4609 13.7778Z"
        fill="white"
      />
      <path
        d="M18.625 47.9997C18.625 34.6928 27.4669 23.4716 39.54 19.8291V13.7778C24.1475 17.5378 12.75 31.4322 12.75 47.9997C12.75 64.5672 24.1475 78.4616 39.54 82.2216V76.1703C27.4669 72.5572 18.625 61.3066 18.625 47.9997Z"
        fill="white"
      />
      <path
        d="M60.6319 54.5506C60.6319 42.5362 41.8025 47.4713 41.8025 40.8325C41.8025 38.4531 43.7119 36.9256 47.3544 36.9256C51.7019 36.9256 53.2 39.0406 53.67 41.89H59.6625C59.1279 36.5426 56.0588 33.1662 50.9382 32.1604V27.4375H45.0632V31.9918C39.4534 32.7062 35.9275 35.973 35.9275 40.8325C35.9275 52.9056 54.7863 48.3819 54.7863 54.9031C54.7863 57.3706 52.4069 59.0156 48.3825 59.0156C43.1244 59.0156 41.3913 56.695 40.745 53.4931H34.8994C35.2781 59.3502 38.8897 63.0159 45.0632 63.9307V68.5625H50.9382V63.9923C56.9633 63.2139 60.6319 59.7089 60.6319 54.5506Z"
        fill="white"
      />
    </svg>
  );
}

/** Ethereum */
export function EthIcon(props: TokenIconProps) {
  return (
    <svg viewBox="0 0 32 32" role="img" aria-label="ETH" {...props}>
      <circle cx="16" cy="16" r="16" fill="#627EEA" />
      <g fill="#FFF" fillRule="nonzero">
        <path fillOpacity=".6" d="M16.5 4v8.87l7.5 3.35z" />
        <path d="M16.5 4 9 16.22l7.5-3.35z" />
        <path fillOpacity=".6" d="M16.5 21.97V28L24 17.62z" />
        <path d="M16.5 28v-6.03L9 17.62z" />
        <path fillOpacity=".2" d="m16.5 20.57 7.5-4.35-7.5-3.35z" />
        <path fillOpacity=".6" d="m9 16.22 7.5 4.35v-7.7z" />
      </g>
    </svg>
  );
}

/** Wrapped Bitcoin */
export function WbtcIcon(props: TokenIconProps) {
  return (
    <svg viewBox="0 0 32 32" role="img" aria-label="WBTC" {...props}>
      <circle cx="16" cy="16" r="16" fill="#F09242" />
      <circle
        cx="16"
        cy="16"
        r="12.6"
        fill="none"
        stroke="#FFF"
        strokeOpacity=".5"
      />
      <path
        fill="#FFF"
        fillRule="nonzero"
        d="M21.16 14.36c.22-1.5-.92-2.3-2.48-2.84l.5-2.03-1.23-.3-.5 1.97c-.32-.08-.66-.16-.99-.23l.5-1.98-1.24-.31-.5 2.03c-.27-.06-.53-.12-.79-.19v-.01l-1.7-.42-.33 1.32s.92.21.9.22c.5.13.59.46.57.72l-.57 2.3c.04.01.08.02.13.04l-.13-.03-.8 3.23c-.06.15-.22.38-.57.3.01.01-.9-.22-.9-.22l-.61 1.41 1.6.4c.3.08.59.15.88.23l-.51 2.05 1.23.3.5-2.03c.34.1.66.18.98.26l-.5 2.02 1.23.3.51-2.04c2.11.4 3.69.24 4.36-1.67.54-1.53-.03-2.41-1.13-2.99.8-.18 1.41-.71 1.57-1.81zm-2.82 3.96c-.38 1.53-2.96.7-3.8.5l.68-2.72c.84.2 3.52.62 3.12 2.22zm.38-3.98c-.35 1.4-2.5.69-3.19.51l.61-2.46c.7.17 2.94.5 2.58 1.95z"
      />
    </svg>
  );
}

/** Lookup by token symbol used in the BalanceCard. */
export const TOKEN_ICONS: Record<
  string,
  (props: TokenIconProps) => React.JSX.Element
> = {
  USDC: UsdcIcon,
  ETH: EthIcon,
  WBTC: WbtcIcon,
};
